import { createContext, useContext, useState, useEffect } from 'react';
import api, { getMe, updateMode as apiUpdateMode } from '../api/client';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser]   = useState(null);
  const [loading, setLoading] = useState(true);
  // View mode: 'simple' (mobile) or 'analytical' (desktop). Cached locally for an
  // instant first paint, then refreshed from the server (the source of truth).
  const [appMode, setAppModeState] = useState(() => localStorage.getItem('ledger_app_mode') || 'simple');

  // Pull the saved mode from the backend and cache it.
  const syncMode = () =>
    getMe()
      .then(res => {
        const m = res.data?.app_mode;
        if (m) {
          setAppModeState(m);
          localStorage.setItem('ledger_app_mode', m);
        }
      })
      .catch(() => {});

  useEffect(() => {
    const token = localStorage.getItem('ledger_token');
    const stored = localStorage.getItem('ledger_user');
    if (token && stored) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(JSON.parse(stored));
      syncMode();
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    const { token, user: u } = res.data;
    persist(token, u);
  };

  const loginWithToken = ({ token, name, email }) => {
    persist(token, { name, email });
  };

  const persist = (token, u) => {
    localStorage.setItem('ledger_token', token);
    localStorage.setItem('ledger_user', JSON.stringify(u));
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(u);
    syncMode();
  };

  // Change the view mode: update instantly (optimistic), save to the server,
  // and roll back if the save fails.
  const setMode = (mode) => {
    const prev = appMode;
    setAppModeState(mode);
    localStorage.setItem('ledger_app_mode', mode);
    apiUpdateMode(mode).catch(() => {
      setAppModeState(prev);
      localStorage.setItem('ledger_app_mode', prev);
    });
  };

  const logout = () => {
    localStorage.removeItem('ledger_token');
    localStorage.removeItem('ledger_user');
    localStorage.removeItem('ledger_app_mode');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithToken, logout, isLoggedIn: !!user, appMode, setMode }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
